<template>
    <div>
        <div>
            <div class="row w-100 my-0 pb-0 col-md-12">
                <div class="col-md-12 mb-1">
                    <p class="text-muted" v-if="!(isSafari && asset.file_extension === 'webp') && !isIE">
                        Transform the current image
                    </p>
                    <p class="text-muted" v-else>
                        Asset-specific transformations are not supported in this browser depending on the file
                        format - the following browsers are supported for all file types (JPEG, PNG, WEBP):
                        <ul class="mt-1">
                            <li>Google Chrome</li>
                            <li>Mozilla Firefox</li>
                            <li>Microsoft Edge</li>
                            <li>Other Chromium-based browsers (Brave, Vivaldi, Opera)</li>
                        </ul>
                    </p>
                </div>
            </div>
            <div class="col-md-12 mb-4" v-if="!(isSafari && asset.file_extension === 'webp') && !isIE">
                <div class="row w-100 my-0 mx-auto p-0 col-md-12">
                    <div class="btn-group">
                        <button data-id="set-transform-compress" @click="transformAction = 'Compress'" class="btn btn-outline-secondary" :class="{ 'active': transformAction === 'Compress' }">
                            Compress
                        </button>
                        <button data-id="set-transform-resize" @click="transformAction = 'Resize'" class="btn btn-outline-secondary" :class="{ 'active': transformAction === 'Resize' }">
                            Resize
                        </button>
                        <button data-id="set-transform-crop" @click="transformAction = 'Crop'" class="btn btn-outline-secondary" :class="{ 'active': transformAction === 'Crop' }">
                            Crop
                        </button>
                        <button data-id="set-transform-convert" @click="transformAction = 'Convert'" class="btn btn-outline-secondary" :class="{ 'active': transformAction === 'Convert' }">
                            Convert
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="!(isSafari && asset.file_extension === 'webp') && !isIE && transformAction === 'Compress'" class="col-md-12">
                <div class="row w-100 my-0 pb-0">
                    <div class="col-md-8">
                        <h4>Compress</h4>
                        <p class="text-muted mt-1" style="max-width: 60ch;">
                            Reduce the fidelity of the image
                        </p>
                    </div>
                </div>
                <div class="row mt-0 mb-4 pb-0 w-100">
                    <div class="col-md-12 mb-2 mt-0">
                        <div class="d-flex flex-column">
                            <label for="">
                                Quality
                            </label>
                            <div style="max-width: 24rem;" class="d-flex flex-column">
                                <select data-id="compression-value" name="" id="" class="form-control" v-model="compressionQuality">
                                    <option value="0.25">
                                        25%
                                    </option>
                                    <option value="0.5">
                                        50%
                                    </option>
                                    <option value="0.65">
                                        65%
                                    </option>
                                    <option value="0.75">
                                        75%
                                    </option>
                                    <option value="0.85">
                                        85%
                                    </option>
                                    <option value="0.9">
                                        90%
                                    </option>
                                </select>
                                <div class="mt-2">
                                    <button data-id="create-transform-compress" class="btn btn-primary" @click="createCompressedImage">
                                        Compress
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="!(isSafari && asset.file_extension === 'webp') && !isIE && transformAction === 'Resize'" class="col-md-12">
                <div class="row w-100 my-0 pb-0">
                    <div class="col-md-6">
                        <h4>Resize</h4>
                        <p class="text-muted mt-1" style="max-width: 60ch;">
                            Adjust the image dimensions
                        </p>
                    </div>
                </div>
                <div class="row mt-0 mb-4 pb-0 w-100">
                    <div class="col-md-12 mb-2 mt-0">
                        <div class="d-flex flex-column">
                            <div class="d-flex">
                                <div class="d-flex flex-column col-md-4 pl-0">
                                    <label for="">
                                        Height (px)
                                    </label>
                                    <input @input="setResizeDimensions($event, 'height')" :disabled="resizeParams.usePercentage" type="number" name="" id="" :value="resizeParams.h" class="form-control">
                                </div>
                                <div class="d-flex flex-column col-md-4">
                                    <label for="">
                                        Width (px)
                                    </label>
                                    <input @input="setResizeDimensions($event, 'width')" :disabled="resizeParams.usePercentage" type="number" name="" id="" :value="resizeParams.w" class="form-control">
                                </div>
                                <div class="d-flex flex-column col-md-4">
                                    <label for="">
                                        Preserve Aspect Ratio
                                    </label>
                                    <input type="checkbox" name="" id="" style="width: 38px;" class="form-control" v-model="resizeParams.preserveAspectRatio">
                                </div>
                            </div>
                            <div class="d-flex mt-2">
                                <div class="d-flex flex-column col-md-4 pl-0">
                                    <label for="">
                                        Use Percentage
                                    </label>
                                    <input type="checkbox" name="" id="" style="width: 38px;" class="form-control" v-model="resizeParams.usePercentage">
                                </div>
                                <div class="d-flex flex-column col-md-4">
                                    <label for="">
                                        Percent
                                    </label>
                                    <input :disabled="!resizeParams.usePercentage" type="number" name="" id="" class="form-control" value="100" @input="setResizeDimensions($event, 'percentage')">
                                </div>
                            </div>
                            <div class="d-flex mt-2">
                                <button data-id="create-transform-resize" class="btn btn-primary mt-2" @click="createResizedImage">
                                    Resize
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="!(isSafari && asset.file_extension === 'webp') && !isIE && transformAction === 'Crop'" class="col-md-12">
                <div class="row w-100 my-0 pb-0">
                    <div class="col-md-6">
                        <h4>Crop</h4>
                        <p class="text-muted mt-1" style="max-width: 60ch;">
                            Trim unwanted parts of the image
                        </p>
                    </div>
                </div>
                <div class="row mt-0 mb-4 pb-0 w-100">
                    <div class="col-md-12 mb-2 mt-0">
                        <div class="img-crop-wrapper">
                            <canvas ref="canvas" id="canvas"></canvas>
                        </div>
                        <div class="text-muted">
                            <div class="d-flex align-items-center justify-content-between" style="width: 10rem;">
                                <span>Actual Width</span>
                                <span class="font-weight-bold">{{ cropReady ? cropParams.w + 'px' : 'Loading...' }}</span>
                            </div>
                            <div class="d-flex align-items-center justify-content-between" style="width: 10rem;">
                                <span>Actual Height</span>
                                <span class="font-weight-bold">{{ cropReady ? cropParams.h + 'px' : 'Loading...' }}</span>
                            </div>
                        </div>
                        <div class="d-flex">
                            <button data-id="create-transform-crop" class="btn btn-primary mt-2" @click="createCroppedImage" :disabled="!cropReady">
                                Crop
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="!(isSafari && asset.file_extension === 'webp') && !isIE && transformAction === 'Convert'" class="col-md-12">
                <div class="row w-100 my-0 pb-0">
                    <div class="col-md-8">
                        <h4>Convert</h4>
                        <p class="text-muted mt-1" style="max-width: 60ch;">
                            Convert image to new type
                        </p>
                    </div>
                </div>
                <div class="row mt-0 mb-4 pb-0 w-100">
                    <div class="col-md-12 mb-2 mt-0">
                        <div class="text-muted">
                            <select name="" id="" class="form-control" v-model="convertAssetType">
                                <option value="jpeg" :selected="asset.file_extension !== 'jpg' && asset.file_extension !== 'jpeg'" v-if="asset.file_extension !== 'jpg' && asset.file_extension !== 'jpeg'">
                                    JPEG
                                </option>
                                <option value="png" :selected="asset.file_extension !== 'png'" v-if="asset.file_extension !== 'png'">
                                    PNG
                                </option>
                                <option value="webp" v-if="asset.file_extension !== 'webp' && !isSafari">
                                    WEBP
                                </option>
                            </select>
                        </div>
                        <div class="d-flex">
                            <button data-id="create-transform-convert" class="btn btn-primary mt-2" @click="createConvertedImage">
                                Convert
                            </button>
                        </div>
                        <div class="mt-1">
                            <p v-if="asset.file_extension === 'webp'|| asset.file_extension === 'png'" class="text-danger mt-1" style="max-width: 60ch;">
                                <strong>Warning!</strong> If converting an asset with an alpha layer (a transparent background)
                                to a JPEG the transparent background will become solid white.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <transition name="fade">
            <div v-if="uiStates.compressModal" class="modal-wrapper">
                <div class="modal-bg" @click="uiStates.compressModal = false"></div>
                <div class="d-flex flex-column modal-inner p-4">
                    <div class="d-flex flex-column align-items-start">
                        <label for="private">Name</label>
                        <input data-id="set-transformed-name" type="text" name="cropped-name" v-model="compressedImage.name" class="mb-2 form-control form-private" />
                    </div>
                    <button data-id="submit-transformed-compress" class="btn my-2 btn-primary" @click="submitTransformedImage">
                        Submit
                    </button>
                </div>
            </div>
        </transition>
        <transition name="fade">
            <div v-if="uiStates.convertModal" class="modal-wrapper">
                <div class="modal-bg" @click="uiStates.convertModal = false"></div>
                <div class="d-flex flex-column modal-inner p-4">
                    <div class="d-flex flex-column align-items-start">
                        <label for="private">Name</label>
                        <input data-id="set-transformed-name" type="text" name="cropped-name" v-model="convertedImage.name" class="mb-2 form-control form-private" />
                    </div>
                    <button data-id="submit-transformed-convert" class="btn my-2 btn-primary" @click="submitTransformedImage">
                        Submit
                    </button>
                </div>
            </div>
        </transition>
        <transition name="fade">
            <div v-if="uiStates.cropModal && croppedImage.image !== null" class="modal-wrapper">
                <div class="modal-bg" @click="uiStates.cropModal = false"></div>
                <div class="d-flex flex-column modal-inner p-4">
                    <p class="font-weight-bold">
                        Cropped Image (Preview)
                    </p>
                    <div class="d-flex align-items-start">
                        <img :src="croppedImage.image" alt="" style="max-width: 24rem; max-height: 24rem; object-fit: contain; border: 1px solid #dedede; border-radius: 2px;">
                    </div>
                    <div class="d-flex flex-column align-items-start">
                        <div class="text-muted mb-4" style="font-size: 12px">
                            <div class="mt-2">
                                <span>
                                    Actual Width:
                                </span>
                                <span class="font-weight-bold">
                                    {{ croppedImage.width }}px
                                </span>
                            </div>
                            <div class="my-2">
                                <span>
                                    Actual Height:
                                </span>
                                <span class="font-weight-bold">
                                    {{ croppedImage.height }}px
                                </span>
                            </div>
                        </div>
                        <label for="private">Name</label>
                        <input data-id="set-transformed-name" type="text" name="cropped-name" v-model="croppedImage.name" class="mb-2 form-control form-private" />
                    </div>
                    <button data-id="submit-transformed-crop" class="btn my-2 btn-primary" @click="submitTransformedImage">
                        Submit
                    </button>
                </div>
            </div>
        </transition>
        <transition name="fade">
            <div v-if="uiStates.resizeModal && resizedImage.image !== null" class="modal-wrapper">
                <div class="modal-bg" @click="uiStates.resizeModal = false"></div>
                <div class="d-flex flex-column modal-inner p-4">
                    <p class="font-weight-bold">
                        Resized Image (Preview)
                    </p>
                    <div class="d-flex align-items-start">
                        <img :src="resizedImage.image" alt="" style="max-width: 24rem; max-height: 24rem; object-fit: contain; border: 1px solid #dedede; border-radius: 2px;">
                    </div>
                    <div class="d-flex flex-column align-items-start">
                        <div class="text-muted mb-4" style="font-size: 12px">
                            <div class="mt-2">
                                <span>
                                    Actual Width:
                                </span>
                                <span class="font-weight-bold">
                                    {{ resizedImage.width }}px
                                </span>
                            </div>
                            <div class="my-2">
                                <span>
                                    Actual Height:
                                </span>
                                <span class="font-weight-bold">
                                    {{ resizedImage.height }}px
                                </span>
                            </div>
                        </div>
                        <label for="private">Name</label>
                        <input data-id="set-transformed-name" type="text" name="cropped-name" v-model="resizedImage.name" class="mb-2 form-control form-private" />
                    </div>
                    <button data-id="submit-transformed-resize" class="btn my-2 btn-primary" @click="submitTransformedImage">
                        Submit
                    </button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from 'axios'

const base_url = window.location.protocol + "//" + window.location.host;

export default {
    name: "asset-transform",
    props: {
        asset: {
            required: true,
            type: Object
        },
        assetTypesMap: {
            required: true,
            type: Object
        },
        isSafari: {
            required: true,
            type: Boolean
        },
        isChromium: {
            required: true,
            type: Boolean
        },
        isIE: {
            required: false,
            type: Boolean
        },
    },
    data() {
        return {
            transformAction: 'Compress',
            uiStates: {
                compressModal: false,
                cropModal: false,
                convertModal: false,
                resizeModal: false,
            },
            compressionQuality: 0.75,
            compressedImage: {
                name: '',
                image: null
            },
            cropParams: {
                x: 0,
                y: 0,
                w: 0,
                h: 0
            },
            cropReady: false,
            croppedImage: {
                name: '',
                image: null,
                width: 0,
                height: 0
            },
            convertAssetType: 'jpeg',
            convertedImage: {
                name: '',
                image: null
            },
            sourceImage: {
                aspectRatio: 0,
                height: 0,
                width: 0
            },
            resizeParams: {
                h: 0,
                w: 0,
                preserveAspectRatio: true,
                percentage: 100,
                usePercentage: false
            },
            resizedImage: {
                name: '',
                image: null,
                width: 0,
                height: 0
            },
        }
    },
    mounted() {
        if (!this.sourceImage.height) {
            let srcImage = new Image();
            let vm = this;
            srcImage.src = this.asset.uri_internal;
            srcImage.onload = function() {
                let aspectHeight = srcImage.naturalHeight,
                    aspectWidth = srcImage.naturalWidth,
                    aspectRatio = aspectHeight / aspectWidth;

                vm.sourceImage.aspectRatio = aspectRatio;
                vm.sourceImage.height = srcImage.naturalHeight;
                vm.sourceImage.width = srcImage.naturalWidth;
                vm.resizeParams.h = srcImage.naturalHeight;
                vm.resizeParams.w = srcImage.naturalWidth;
            }
        }

        if (this.asset.file_extension === 'jpeg' || this.asset.file_extension === 'jpg') {
            this.convertAssetType = 'png'
        }
    },
    methods: {
        createCompressedImage() {
            let vm = this;
            let srcImage = new Image();

            axios.get(base_url + '/pimapi/assets/' + this.asset.id + '/base64')
                .then(function(response) {
                    srcImage.src = response.data.payload.base64;
                }.bind(this))
                .catch(function(err) {
                    console.log(err);

                    this.$emit("alert", {
                        status: "alert-danger",
                        message: err.response.data.message || "There was an error, please try again later."
                    });
                }.bind(this));

            srcImage.onload = function() {
                let newImageCanvas = document.createElement('canvas');
                newImageCanvas.width = srcImage.naturalWidth;
                newImageCanvas.height = srcImage.naturalHeight;

                let newImageCtx = newImageCanvas.getContext('2d');

                newImageCtx.drawImage(srcImage, 0, 0, srcImage.naturalWidth, srcImage.naturalHeight);

                if (vm.asset.file_extension === "jpg" || vm.asset.file_extension === "jpeg") {
                    generateImage(newImageCanvas, 'image/jpeg');
                } else if (vm.asset.file_extension === 'webp') {
                    if (this.isSafari || this.isIE) {
                        this.$emit("alert", {
                            status: "alert-danger",
                            message: "Webp format is not supported in this browser"
                        });
                    } else {
                        generateImage(newImageCanvas, 'image/webp');
                    }
                } else {
                    generateImage(newImageCanvas, 'image/png');
                }

                function generateImage(canvas, format) {
                    const fileName = vm.asset.name.split('.' + vm.asset.file_extension)[0];
                    const dataURL = canvas.toDataURL(format, parseFloat(vm.compressionQuality));

                    let date = new Date();
                    date = date.toISOString().split('T')[0];

                    vm.compressedImage.image = dataURL;
                    vm.compressedImage.name = `${fileName} (Compressed - ${date}).${vm.asset.file_extension}`;
                    vm.uiStates.compressModal = true;
                }
            }
        },
        createResizedImage() {
            let vm = this;
            let srcImage = new Image();

            axios.get(base_url + '/pimapi/assets/' + this.asset.id + '/base64')
                .then(function(response) {
                    srcImage.src = response.data.payload.base64;
                }.bind(this))
                .catch(function(err) {
                    console.log(err);

                    this.$emit("alert", {
                        status: "alert-danger",
                        message: err.response.data.message || "There was an error, please try again later."
                    });
                }.bind(this));

            srcImage.onload = function() {
                let newImageCanvas = document.createElement('canvas');
                let height = vm.resizeParams.usePercentage ? (vm.resizeParams.h * vm.resizeParams.percentage) : vm.resizeParams.h;
                let width = vm.resizeParams.usePercentage ? (vm.resizeParams.w * vm.resizeParams.percentage) : vm.resizeParams.w;

                newImageCanvas.width = width;
                newImageCanvas.height = height;

                let newImageCtx = newImageCanvas.getContext('2d');

                newImageCtx.drawImage(srcImage, 0, 0, width, height);

                if (vm.asset.file_extension === "jpg" || vm.asset.file_extension === "jpeg") {
                    generateImage(newImageCanvas, 'image/jpeg');
                } else if (vm.asset.file_extension === 'webp') {
                    if (this.isSafari || this.isIE) {
                        this.$emit("alert", {
                            status: "alert-danger",
                            message: "Webp format is not supported in this browser"
                        });
                    } else {
                        generateImage(newImageCanvas, 'image/webp');
                    }
                } else {
                    generateImage(newImageCanvas, 'image/png');
                }

                function generateImage(canvas, format) {
                    const fileName = vm.asset.name.split('.' + vm.asset.file_extension)[0];
                    const dataURL = canvas.toDataURL(format);

                    let date = new Date();
                    date = date.toISOString().split('T')[0];

                    vm.resizedImage.image = dataURL;
                    vm.resizedImage.name = `${fileName} (Resized - ${date}).${vm.asset.file_extension}`;
                    vm.resizedImage.width = canvas.width;
                    vm.resizedImage.height = canvas.height;
                    vm.uiStates.resizeModal = true;
                }
            }
        },
        createConvertedImage() {
            let vm = this;
            let srcImage = new Image();

            axios.get(base_url + '/pimapi/assets/' + this.asset.id + '/base64')
                .then(function(response) {
                    srcImage.src = response.data.payload.base64;
                }.bind(this))
                .catch(function(err) {
                    console.log(err);

                    this.$emit("alert", {
                        status: "alert-danger",
                        message: err.response.data.message || 'There was an error, please try again later.'
                    });
                }.bind(this));

            srcImage.onload = function() {
                let srcHeight = srcImage.naturalHeight;
                let srcWidth = srcImage.naturalWidth;

                let newImageCanvas = document.createElement('canvas');
                let newImageCtx = newImageCanvas.getContext('2d');

                newImageCanvas.width = srcWidth;
                newImageCanvas.height = srcHeight;

                if (vm.asset.file_extension === "jpg" || vm.asset.file_extension === "jpeg") {
                    newImageCtx.fillStyle = '#fff';
                    newImageCtx.fillRect(0, 0, srcWidth, srcHeight);
                }

                newImageCtx.drawImage(srcImage, 0, 0, srcWidth, srcHeight);

                if (vm.convertAssetType === 'webp') {
                    if (this.isSafari || this.isIE) {
                        this.$emit("alert", {
                            status: "alert-danger",
                            message: "Webp format is not supported in this browser"
                        });
                    } else {
                        generateImage(newImageCanvas, 'image/webp');
                    }
                } else {
                    generateImage(newImageCanvas, `image/${vm.convertAssetType}`);
                }
            };

            function generateImage(canvas, format) {
                const fileName = vm.asset.name.split('.' + vm.asset.file_extension)[0];
                const dataURL = canvas.toDataURL(format);

                let date = new Date();
                date = date.toISOString().split('T')[0];

                vm.convertedImage.image = dataURL;
                vm.convertedImage.name = `${fileName} (Converted - ${date}).${vm.convertAssetType}`;
                vm.uiStates.convertModal = true;
            }
        },
        createCroppedImage() {
            let vm = this;
            let srcImage = new Image();

            axios.get(base_url + '/pimapi/assets/' + this.asset.id + '/base64')
                .then(function(response) {
                    srcImage.src = response.data.payload.base64;
                }.bind(this))
                .catch(function(err) {
                    console.log(err);

                    this.$emit("alert", {
                        status: "alert-danger",
                        message: err.response.data.message || 'There was an error, please try again later.'
                    });
                }.bind(this));

            srcImage.onload = function() {
                let newImageCanvas = document.createElement('canvas');
                let newImageCtx = newImageCanvas.getContext('2d');

                let { x, y, w, h} = vm.cropParams;

                newImageCanvas.width = w;
                newImageCanvas.height = h;

                newImageCtx.drawImage(srcImage, x, y, w, h, 0, 0, w, h);

                if (vm.asset.file_extension === "jpg" || vm.asset.file_extension === "jpeg") {
                    generateImage(newImageCanvas, 'image/jpeg');
                } else if (vm.asset.file_extension === 'webp') {
                    if (this.isSafari || this.isIE) {
                        this.$emit("alert", {
                            status: "alert-danger",
                            message: "Webp format is not supported in this browser"
                        });

                    } else {
                        generateImage(newImageCanvas, 'image/webp');
                    }
                } else {
                    generateImage(newImageCanvas, 'image/png');
                }

                function generateImage(canvas, format) {
                    const fileName = vm.asset.name !== null ? vm.asset.name.split('.' + vm.asset.file_extension)[0] : 'Untitled Cropped Image';
                    const dataURL = canvas.toDataURL(format);

                    let date = new Date();
                    date = date.toISOString().split('T')[0];

                    vm.croppedImage.image = dataURL;
                    vm.croppedImage.name = `${fileName} (Crop - ${date}).${vm.asset.file_extension}`;
                    vm.croppedImage.width = canvas.width;
                    vm.croppedImage.height = canvas.height;
                    vm.uiStates.cropModal = true;
                }
            }
        },
        setCropParams(object) {
            this.cropParams.x = object.x;
            this.cropParams.y = object.y;
            this.cropParams.w = object.w;
            this.cropParams.h = object.h;
        },
        setResizeDimensions(e, input) {
            if (input === 'height') {
                this.resizeParams.h = e.target.value;

                if (this.resizeParams.preserveAspectRatio) {
                    this.resizeParams.w = parseFloat((this.resizeParams.h / this.sourceImage.aspectRatio).toFixed(2))
                }
            }
            if (input === 'width') {
                this.resizeParams.w = e.target.value;

                if (this.resizeParams.preserveAspectRatio) {
                    this.resizeParams.h = parseFloat((this.resizeParams.w / this.sourceImage.aspectRatio).toFixed(2))
                }
            }
            if (input === 'percentage') {
                if (e.target.value > 500 || e.target.value <= 0) {
                    this.$emit("alert", {
                        status: "alert-danger",
                        message: "Number out of bounds, please use a number between 0 and 500"
                    });
                }

                this.resizeParams.percentage = e.target.value / 100
            }
        },
        async submitTransformedImage() {
            let formData = new FormData();
            let payloadImage;

            if (this.transformAction === 'Crop') {
                payloadImage = this.croppedImage;
            }
            if (this.transformAction === 'Compress') {
                payloadImage = this.compressedImage;
            }
            if (this.transformAction === 'Resize') {
                payloadImage = this.resizedImage;
            }
            if (this.transformAction === 'Convert') {
                payloadImage = this.convertedImage;
            }

            const b64toBlob = (url) => fetch(url).then(res => res.blob());

            b64toBlob(payloadImage.image).then((blob) => {
                let payloadImageWithExt = payloadImage.name +"."+ this.asset.file_extension;
                let file = new File([blob], payloadImageWithExt, { type: blob.type });
                formData.append('files', file);
                formData.append('isPrivate', this.asset.private);

                axios.post(base_url + '/pimapi/assets', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        },
                    }).then(function(response) {
                        let newVersionId = response.data.payload[0];

                        return axios.put(base_url + '/pimapi/assets/' + this.asset.id + '/version', {
                            assetId: newVersionId
                        })
                    }.bind(this))
                    .then(function(response) {
                        this.uiStates.compressModal = false;
                        this.uiStates.convertModal = false;
                        this.uiStates.cropModal = false;
                        this.uiStates.resizeModal = false;

                        this.$parent.alert = {
                            status: "alert-success",
                            message: "Success, transformed asset added as version."
                        };
                        this.$emit("refresh-transform", response.data.payload);
                    }.bind(this))
                    .catch(function(err) {
                        console.log(err);

                        this.$parent.alert = {
                            status: "alert-danger",
                            message: err.response.data.message
                        };
                    }.bind(this));
            });
        },
    },
    watch: {
        uiStates: {
            handler: function() {
                if (this.uiStates.convertModal || this.uiStates.resizeModal || this.uiStates.compressModal || this.uiStates.cropModal) {
                    document.body.style.position = 'fixed';
                    document.body.style.top = `-${window.scrollY}px`;
                } else {
                    const scrollY = document.body.style.top;
                    document.body.style.position = '';
                    document.body.style.top = '';
                    window.scrollTo(0, parseInt(scrollY || '0') * -1);
                }
            }
        },
        transformAction: {
            handler: function() {
                if (this.transformAction === 'Crop') {
                    let vm = this;
                    let srcImage = new Image();

                    srcImage.src = this.asset.uri_internal;
                    srcImage.onload = function() {
                        let aspectHeight = srcImage.naturalHeight,
                            aspectWidth = srcImage.naturalWidth,
                            aspectRatio = aspectHeight / aspectWidth;

                        let mouseX,
                            mouseY,
                            closeEnough = 20,
                            canvasWidth = 442,
                            dragTL,
                            dragBL,
                            dragTR,
                            dragBR,
                            dragAll = false,
                            selection = {
                                x: 20,
                                y: 20,
                                w: 100,
                                h: 100
                            };

                        let canvasHeight = canvasWidth * aspectRatio;
                        let heightMultiplier = aspectHeight / canvasHeight;
                        let widthMultiplier = aspectWidth / canvasWidth;
                        let canvas = document.getElementById('canvas');
                        let context = canvas.getContext('2d', { preserveDrawingBuffer: true });

                        canvas.width = canvasWidth;
                        canvas.height = canvasHeight;
                        canvas.style.border = '1px solid #dedede';
                        canvas.style.borderRadius = '2px';

                        context.drawImage(srcImage, 0, 0, canvasWidth, canvasHeight);

                        function getMousePos(canvas, e) {
                            let element = canvas.getBoundingClientRect();

                            return {
                                x: (e.clientX - element.left) / (element.right - element.left) * canvas.width,
                                y: (e.clientY - element.top) / (element.bottom - element.top) * canvas.height
                            };
                        };

                        function checkCloseEnough(p1, p2) {
                            return Math.abs(p1 - p2) < closeEnough;
                        }

                        function drawCircle(x, y, radius) {
                            context.fillStyle = "#00b1d9";
                            context.beginPath();
                            context.arc(x, y, radius, 0, 2 * Math.PI);
                            context.fill();
                        }

                        function mouseDown(e) {
                            let mousePos = getMousePos(canvas, e);
                            mouseX = mousePos.x
                            mouseY = mousePos.y;

                            if (selection.w === undefined) {
                                selection.x = mouseY;
                                selection.y = mouseX;
                                dragBR = true;
                            } else if (checkCloseEnough(mouseX, selection.x) && checkCloseEnough(mouseY, selection.y)) {
                                dragTL = true;
                            } else if (checkCloseEnough(mouseX, selection.x + selection.w) && checkCloseEnough(mouseY, selection.y)) {
                                dragTR = true;
                            } else if (checkCloseEnough(mouseX, selection.x) && checkCloseEnough(mouseY, selection.y + selection.h)) {
                                dragBL = true;
                            } else if (checkCloseEnough(mouseX, selection.x + selection.w) && checkCloseEnough(mouseY, selection.y + selection.h)) {
                                dragBR = true;
                            } else if (mouseX > selection.x && mouseX < selection.x + selection.w && mouseY > selection.y && mouseY < selection.y + selection.h) {
                                dragAll = true;
                            }

                            context.clearRect(0, 0, canvas.width, canvas.height);
                            draw();
                        }

                        function mouseMove(e) {
                            let mousePos = getMousePos(canvas, e);

                            mouseX = mousePos.x;
                            mouseY = mousePos.y;

                            if (dragTL) {
                                selection.w += selection.x - mouseX;
                                selection.h += selection.y - mouseY;
                                selection.x = mouseX;
                                selection.y = mouseY;
                            } else if (dragTR) {
                                selection.w = Math.abs(selection.x - mouseX);
                                selection.h += selection.y - mouseY;
                                selection.y = mouseY;
                            } else if (dragBL) {
                                selection.w += selection.x - mouseX;
                                selection.h = Math.abs(selection.y - mouseY);
                                selection.x = mouseX;
                            } else if (dragBR) {
                                selection.w = Math.abs(selection.x - mouseX);
                                selection.h = Math.abs(selection.y - mouseY);
                            } else if (dragAll) {
                                selection.y = mouseY;
                                selection.x = mouseX;
                            }

                            handleSaveParams();
                            context.clearRect(0, 0, canvas.width, canvas.height);
                            draw();
                        }

                        function handleNegativeValue(x, y) {
                            return Math.floor(x * y) < 0 ? 0 : Math.floor(x * y);
                        }

                        function handleSaveParams() {
                            let upscaledParams = {};

                            upscaledParams.x = handleNegativeValue(selection.x, widthMultiplier);
                            upscaledParams.y = handleNegativeValue(selection.y, heightMultiplier);
                            upscaledParams.w = handleNegativeValue(selection.w, widthMultiplier);
                            upscaledParams.h = handleNegativeValue(selection.h, heightMultiplier);

                            vm.setCropParams(upscaledParams);
                        }

                        function mouseUp() {
                            dragTL = dragTR = dragBL = dragBR = dragAll = false;
                        }

                        function drawHandles() {
                            if (!vm.cropReady) {
                                handleSaveParams();
                                vm.cropReady = true;
                            }

                            drawCircle(selection.x, selection.y, 5);
                            drawCircle(selection.x + selection.w, selection.y, 5);
                            drawCircle(selection.x + selection.w, selection.y + selection.h, 5);
                            drawCircle(selection.x, selection.y + selection.h, 5);
                        }

                        function draw() {
                            context.drawImage(srcImage, 0, 0, canvasWidth, canvasHeight);
                            context.fillStyle = "#77777733";
                            context.fillRect(selection.x, selection.y, selection.w, selection.h);
                            drawHandles();
                        }

                        canvas.addEventListener('mousedown', mouseDown, false);
                        canvas.addEventListener('mouseup', mouseUp, false);
                        canvas.addEventListener('mousemove', mouseMove, false);
                        canvas.addEventListener('mouseleave', mouseUp, false);
                    }
                }
            }
        }
    }
}
</script>

<style lang="scss">
    .modal-wrapper {
        z-index: 1001;
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;

        .modal-bg {
            background: rgba(255,255,255,0.8);
            width: 100%;
            height: 100%;
            position: absolute;
            z-index: 1001;
        }

        .modal-inner {
            border-radius: 5px;
            border: 1px solid #dedede;
            z-index: 1002;
            position: absolute;
            background: white;
            min-width: 24rem;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 1px 3px 8px 2px rgba(0,0,0,0.25);
        }
    }
</style>
